import useRpc from "@/src/useRpc"
import LoadingSpinner from "./LoadingSpinner"
import { useCallback, useEffect, useState } from "react"
import Modal from "./Modal"
import { sendMessage, useMessage } from "@/src/useMessage"
import ProgressBar from "./ProgressBar"
import rpc, { rpcTask } from "@/src/rpc"
import TopicPicker from "./TopicPicker"
import { sleep } from "@/src/misc"
import SimpleDate from "@/lib/SimpleDate"
import EditIcon from "./icons/EditIcon"
import TrashIcon from "./icons/TrashIcon"


export default function ContentPlansScreen({org}) {
  const [plans, plansError, reloadPlans] = useRpc('contentPlans.all')
  const [topics] = useRpc('topics.all')
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)

  const handleRefresh = useCallback(() => reloadPlans(), [reloadPlans])
  useMessage('refreshContentPlans', handleRefresh)

  if (!plans || !topics) return <LoadingSpinner size="normal" />

  const defaultPlan = plans.find(p => p.isDefault)
  const specificPlans = plans.filter(p => !p.isDefault)
  const anyBuilding = plans.some(p => p.status === 'building')

  return <>
    <div className="flex flex-col bg-f3f3f3 absolute-fill relative overflow-hidden">
      <div className="flex-grow overflow-auto-y p-4">

        {/* Building banner */}
        { anyBuilding &&
          <div className="flex-row flex-center gap-2 bg-white rounded-lg shadow-sm mb-3 p-3">
            <LoadingSpinner size="tiny" />
            <span className="gray text-sm">Building content plan...</span>
          </div>
        }

        {/* Default Plan */}
        { defaultPlan &&
          <ContentPlanCard
            plan={defaultPlan}
            org={org}
            topics={topics}
            onEdit={() => setEditing(defaultPlan.id)}
            onReload={reloadPlans}
          />
        }

        {/* Specific Plans */}
        { specificPlans.map(plan =>
          <ContentPlanCard
            key={plan.id}
            plan={plan}
            org={org}
            topics={topics}
            onEdit={() => setEditing(plan.id)}
            onDelete={async () => {
              await rpc('contentPlans.destroy', {id: plan.id}, {throwErrors: true})
              await reloadPlans()
              sendMessage('refreshCalendar')
            }}
            onReload={reloadPlans}
          />
        )}

        {/* Add Plan Button */}
        <div className="text-center mt-4">
          <button className="button is-primary" onClick={() => setCreating(true)}>
            Add a Plan
          </button>
        </div>
      </div>
    </div>

    {/* Edit Modal */}
    { editing &&
      <ContentPlanModal
        plan={plans.find(p => p.id === editing)}
        org={org}
        allTopics={topics}
        onClose={() => setEditing(null)}
        onSave={async () => {
          setEditing(null)
          await reloadPlans()
          sendMessage('refreshContentPlans')
        }}
      />
    }

    {/* Create Modal */}
    { creating &&
      <ContentPlanModal
        org={org}
        allTopics={topics}
        onClose={() => setCreating(false)}
        onSave={async () => {
          setCreating(false)
          await reloadPlans()
          sendMessage('refreshContentPlans')
        }}
      />
    }
  </>
}


function niceTitle(plan) {
  if (plan.isDefault) return 'Social Media Plan'
  if (!plan.startDate || !plan.endDate) return plan.name || 'Untitled Plan'

  const start = new SimpleDate(plan.startDate)
  const end = new SimpleDate(plan.endDate)

  // If it spans exactly one calendar month
  if (start.dayOfMonth === 1 && end.month === start.month && end.year === start.year) {
    const lastDay = start.addMonths(1).addDays(-1)
    if (end.dayOfMonth === lastDay.dayOfMonth) {
      return `${start.monthName} ${start.year}`
    }
  }

  // If same month
  if (start.month === end.month && start.year === end.year) {
    return `${start.monthName} ${start.dayOfMonth}-${end.dayOfMonth}, ${start.year}`
  }

  return `${start.toEnglish()} - ${end.toEnglish()}`
}


function truncateWords(text, maxWords) {
  if (!text) return { text: '', truncated: false }
  const words = text.split(/\s+/)
  if (words.length <= maxWords) return { text, truncated: false }
  return { text: words.slice(0, maxWords).join(' ') + '...', truncated: true }
}


function ContentPlanCard({plan, org, topics, onEdit, onDelete, onReload}) {
  const [showFullInstructions, setShowFullInstructions] = useState(false)
  const planTopics = topics.filter(t => plan.topicIds?.includes(t.id))

  const {postsPerWeekMin, postsPerWeekMax} = plan
  let frequencyText = ''
  if (postsPerWeekMin === postsPerWeekMax) {
    frequencyText = `${postsPerWeekMin} post${postsPerWeekMin === 1 ? '' : 's'} per week`
  } else {
    frequencyText = `${postsPerWeekMin}-${postsPerWeekMax} posts per week`
  }

  const isRetired = plan.status === 'retired'
  const isBuilding = plan.status === 'building'

  const title = niceTitle(plan)
  const { text: instructionPreview, truncated: instructionsTruncated } = truncateWords(plan.instructions, 20)

  return <div className={`bg-white rounded-lg shadow-sm mb-3 ${isRetired ? 'opacity-50' : ''}`}>

    {/* Building progress bar */}
    { isBuilding &&
      <div className="bg-info-light rounded-t-lg px-4 py-2">
        <p className="text-sm mb-1 gray">Building...</p>
        <BuildingProgress plan={plan} onDone={onReload} />
      </div>
    }

    <div className="p-4">
      <div className="flex-row flex-justify-between" style={{alignItems: 'flex-start'}}>
        <div style={{flex: 1}}>
          <strong>{title}</strong>
          <p className="gray text-sm">{frequencyText}</p>
        </div>
        <div className="flex-row" style={{flexShrink: 0, gap: '2px'}}>
          { isRetired &&
            <span className="tag is-warning is-light">Retired</span>
          }
          { !plan.isDefault && onDelete &&
            <button
              className="plan-icon-btn"
              title="Delete"
              onClick={onDelete}
              style={{color: '#cc3333'}}
            >
              <TrashIcon />
            </button>
          }
          { !isRetired &&
            <button
              className="plan-icon-btn"
              title="Edit"
              onClick={onEdit}
              style={{color: '#3273dc'}}
            >
              <EditIcon />
            </button>
          }
        </div>
      </div>

      { planTopics.length > 0 &&
        <ul className="topic-list text-sm mb-2 mt-2">
          { planTopics.map(t =>
            <li key={t.id}>{t.displayName || t.name}</li>
          )}
        </ul>
      }

      { plan.instructions &&
        <p className="gray text-sm italic mt-1">
          "{showFullInstructions ? plan.instructions : instructionPreview}"
          { instructionsTruncated &&
            <button
              className="button is-small is-white ml-1 text-sm"
              style={{textDecoration: 'underline', padding: 0, height: 'auto', border: 'none'}}
              onClick={() => setShowFullInstructions(!showFullInstructions)}
            >
              {showFullInstructions ? 'show less' : 'show more'}
            </button>
          }
        </p>
      }
    </div>

    <style jsx>{`
      .plan-icon-btn {
        background: none;
        border: none;
        box-shadow: none;
        cursor: pointer;
        padding: 4px 6px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        opacity: 0.35;
        transition: opacity 0.15s, background 0.15s;
      }
      .plan-icon-btn:hover {
        opacity: 1;
        background: #f0f0f0;
      }
      ul.topic-list {
        list-style-type: disc;
        padding-left: 1.2em;
        margin: 0;
        color: #555;
      }
      .plan-icon-btn :global(svg) {
        width: 20px;
        height: 20px;
      }
    `}</style>
  </div>
}


function BuildingProgress({plan, onDone}) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let cancelled = false

    async function poll() {
      while (!cancelled) {
        await sleep(1500)
        if (cancelled) break

        try {
          const data = await rpc('contentPlans.find', {id: plan.id}, {throwErrors: true})
          if (data.buildingTask) {
            setProgress(data.buildingTask.percentComplete || 0)
          }
          if (data.status !== 'building') {
            setProgress(100)
            await sleep(500)
            sendMessage('refreshCalendar')
            sendMessage('refreshContentPlans')
            if (onDone) onDone()
            break
          }
        } catch (err) {
          console.error('Error polling content plan status', err)
          break
        }
      }
    }

    poll()
    return () => { cancelled = true }
  }, [plan.id])

  return <ProgressBar value={progress} />
}


// Date range preset options for the modal
function getDatePresets() {
  const today = new SimpleDate()
  const currentMonthStart = new SimpleDate(`${today.year}-${String(today.month).padStart(2, '0')}-01`)
  const currentMonthEnd = currentMonthStart.addMonths(1).addDays(-1)
  const nextMonthStart = currentMonthStart.addMonths(1)
  const nextMonthEnd = nextMonthStart.addMonths(1).addDays(-1)

  // Next week: find Monday of next week
  const daysUntilMonday = (8 - today.dayOfWeek) % 7 || 7
  const nextWeekStart = today.addDays(daysUntilMonday)
  const nextWeekEnd = nextWeekStart.addDays(6)

  return [
    {
      label: `This Month (${today.monthName})`,
      startDate: currentMonthStart.toString(),
      endDate: currentMonthEnd.toString(),
    },
    {
      label: `Next Week (${nextWeekStart.monthName} ${nextWeekStart.dayOfMonth}-${nextWeekEnd.dayOfMonth})`,
      startDate: nextWeekStart.toString(),
      endDate: nextWeekEnd.toString(),
    },
    {
      label: `Next Month (${nextMonthStart.monthName})`,
      startDate: nextMonthStart.toString(),
      endDate: nextMonthEnd.toString(),
    },
    {
      label: 'Custom date range',
      startDate: '',
      endDate: '',
      isCustom: true,
    },
  ]
}


const DEFAULT_INSTRUCTIONS = 'Keep the tone friendly and professional.'


function ContentPlanModal({plan, org, allTopics, onClose, onSave}) {
  const isCreate = !plan
  const isDefault = plan?.isDefault

  const presets = getDatePresets()

  // Determine initial preset selection
  function initialPreset() {
    if (isDefault || isCreate) return 0 // Default to "This Month"
    const idx = presets.findIndex(p => p.startDate === plan.startDate && p.endDate === plan.endDate)
    if (idx >= 0) return idx
    return presets.length - 1 // Custom
  }

  const [selectedPreset, setSelectedPreset] = useState(initialPreset())
  const [customStartDate, setCustomStartDate] = useState(plan?.startDate || '')
  const [customEndDate, setCustomEndDate] = useState(plan?.endDate || '')
  const [frequencyRange, setFrequencyRange] = useState(
    `${plan?.postsPerWeekMin ?? 4}-${plan?.postsPerWeekMax ?? 5}`
  )
  const [instructions, setInstructions] = useState(
    plan?.instructions ?? (isCreate ? DEFAULT_INSTRUCTIONS : '')
  )
  const [selectedTopicIds, setSelectedTopicIds] = useState(
    plan?.topicIds || allTopics.filter(t => t.enabled).map(t => t.id)
  )
  const [localTopics, setLocalTopics] = useState(
    allTopics.map(t => ({...t, enabled: (plan?.topicIds || allTopics.filter(x => x.enabled).map(x => x.id)).includes(t.id)}))
  )
  const [saving, setSaving] = useState(false)

  const today = new SimpleDate()
  const tomorrow = today.addDays(1)
  const maxDate = today.addMonths(12)

  const isCustom = presets[selectedPreset]?.isCustom

  // Compute effective start/end dates
  const startDate = isCustom ? customStartDate : presets[selectedPreset]?.startDate
  const endDate = isCustom ? customEndDate : presets[selectedPreset]?.endDate

  // Sync localTopics to selectedTopicIds
  useEffect(() => {
    setSelectedTopicIds(localTopics.filter(t => t.enabled).map(t => t.id))
  }, [localTopics])


  async function handleSave() {
    const min = parseInt(frequencyRange.split('-')[0], 10)
    const max = parseInt(frequencyRange.split('-')[1], 10)
    const topicIds = selectedTopicIds

    if (!isDefault && !startDate) {
      sendMessage('error', 'Please select a date range')
      return
    }
    if (!isDefault && !endDate) {
      sendMessage('error', 'Please select a date range')
      return
    }

    setSaving(true)

    try {
      if (isCreate) {
        await rpcTask('contentPlans.create', {
          name: '',
          startDate: startDate || null,
          endDate: endDate || null,
          postsPerWeekMin: min,
          postsPerWeekMax: max,
          topicIds,
          instructions,
        })
      } else {
        await rpc('contentPlans.update', {
          id: plan.id,
          startDate: isDefault ? undefined : (startDate || undefined),
          endDate: isDefault ? undefined : (endDate || undefined),
          postsPerWeekMin: min,
          postsPerWeekMax: max,
          topicIds,
          instructions,
          rebuild: true,
        }, {throwErrors: true, suppressTaskWarning: true})
      }

      // Close modal immediately — building progress is shown in the plan list
      onSave()
    } catch (err) {
      console.error(err)
      sendMessage('error', err.message || 'Failed to save content plan')
      setSaving(false)
    }
  }


  return <Modal visible onClose={onClose}>
    <div className="plan-modal-container flex-col" style={{maxHeight: '86vh', maxWidth: '95vw'}}>
        <>
          <div className="px-5 py-3 shadow bg-white z-up">
            <h3 className="text-center">
              {isCreate ? 'New Content Plan' : isDefault ? 'Edit Social Media Plan' : 'Edit Plan'}
            </h3>
          </div>

          <div className="flex-grow bg-f3f3f3 overflow-auto-y" style={{overflowX: 'hidden'}}>
            <div className="plan-modal-fields px-5 py-3">
              {/* Left column: date range + frequency */}
              <div className="plan-modal-left">
                {/* Frequency */}
                <div className="mb-3">
                  <label className="label">Frequency</label>
                  <div className="select is-fullwidth">
                    <select value={frequencyRange} onChange={e => setFrequencyRange(e.target.value)}>
                      <option value="1-1">1 post per week</option>
                      <option value="2-3">2-3 posts per week</option>
                      <option value="4-5">4-5 posts per week</option>
                      <option value="6-7">6-7 posts per week</option>
                      <option value="7-7">7 posts per week</option>
                    </select>
                  </div>
                </div>

                {/* Date Range (not for default plan) */}
                { !isDefault &&
                  <div className="mb-3">
                    <label className="label">Date Range</label>
                    <div className="date-range-row">
                      <div className="date-range-field">
                        { isCustom && <label className="label is-small mb-0" style={{visibility: 'hidden'}}>&nbsp;</label> }
                        <div className="select">
                          <select
                            value={selectedPreset}
                            onChange={e => setSelectedPreset(parseInt(e.target.value, 10))}
                          >
                            { presets.map((preset, idx) =>
                              <option key={idx} value={idx}>{preset.label}</option>
                            )}
                          </select>
                        </div>
                      </div>

                      { isCustom &&
                        <div className="date-range-custom">
                          <div className="date-range-field">
                            <label className="label is-small mb-0">Start</label>
                            <input
                              type="date"
                              className="input"
                              value={customStartDate}
                              onChange={e => setCustomStartDate(e.target.value)}
                              min={tomorrow.toString()}
                              max={maxDate.toString()}
                            />
                          </div>
                          <div className="date-range-field">
                            <label className="label is-small mb-0">End</label>
                            <input
                              type="date"
                              className="input"
                              value={customEndDate}
                              onChange={e => setCustomEndDate(e.target.value)}
                              min={customStartDate || tomorrow.toString()}
                              max={maxDate.toString()}
                            />
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>

              {/* Right column: instructions */}
              <div className="plan-modal-right">
                <label className="label">Rules for Captions</label>
                <textarea
                  className="textarea plan-modal-textarea"
                  placeholder="For example... Use a casual tone. Always end with a question."
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                />
              </div>
            </div>

            {/* Topics */}
            <TopicPicker
              onChange={setLocalTopics}
              topics={localTopics}
              allowNew={!!org.aiEnabled}
            />
          </div>

          {/* Actions */}
          <div className="shadow-top bg-white py-2 z-up">
            <div className="flex-row flex-center gap-2">
              <button className="button is-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : isCreate ? 'Create Plan' : 'Save & Rebuild'}
              </button>
              <button className="button" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </>

      <style jsx>{`
        .plan-modal-container {
          width: 90vw;
          max-width: 900px;
        }
        .plan-modal-fields {
          display: flex;
          gap: 1.5rem;
        }
        .plan-modal-left {
          flex: 1;
          min-width: 0;
        }
        .plan-modal-right {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .plan-modal-right :global(.plan-modal-textarea) {
          flex: 1;
          min-height: 80px;
          resize: vertical;
        }
        .date-range-row {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
        }
        .date-range-custom {
          display: flex;
          gap: 0.5rem;
        }
        .date-range-field :global(.label) {
          font-weight: 400;
          font-size: 11px;
          color: #888;
          margin-bottom: 2px;
        }
        @media (max-width: 640px) {
          .plan-modal-container {
            width: 100%;
          }
          .plan-modal-fields {
            flex-direction: column;
            gap: 0;
          }
          .date-range-row {
            flex-direction: column;
            align-items: stretch;
          }
          .date-range-custom {
            margin-top: 0.5rem;
          }
        }
      `}</style>
    </div>
  </Modal>
}
